import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const usageAnalyticsGuide: AdminGuide = {
  id: "usage-analytics",
  title: "Usage Analytics (Admin): activity minutes and activity status",
  category: "Customer Support",
  audience: ["Support", "Ops", "CS"],
  summary:
    "Use usage minutes to understand customer activity without relying on login timestamps alone.",
  keywords: [
    "usage",
    "usage analytics",
    "activity status",
    "active minutes",
    "inactive",
    "cancelled",
    "never logged in",
    "csv export",
  ],
  location: "Admin",
  risk: "low",
  sourceDocs: [GUIDE_SOURCE_DOCS.USAGE_TRACKING],
  safetyNotes: [
    "Usage tracking is designed to avoid logging sensitive data. Treat exported usage CSVs as operational data and share only internally as needed.",
    "Demo usage is excluded by default from customer usage analytics unless explicitly filtered.",
    "Support sessions are excluded from customer usage tracking to avoid polluting analytics.",
  ],
  whatThisHelpsYouDo: [
    "Check whether a customer (or org) has been actively using the product recently.",
    "Export usage data to CSV for analysis and investigation.",
    "Interpret activity buckets (active/inactive/cancelled/never logged in) consistently across admin tooling.",
  ],
  whenToUse: [
    "Support asks: “Is the customer actually using the app?”",
    "You need to verify whether a spike/drop in activity is real or a data lag.",
    "You need a CSV export for analysis (internal-only).",
  ],
  whenNotToUse: [
    "You need to debug a specific broken workflow (use Bug Tracker + correlation IDs).",
    "You are making billing decisions (use billing + Stripe tooling).",
  ],
  beforeYouStart: [
    "A date window you want to investigate (start date and end date).",
    "If you are looking for a specific organisation: organisationId.",
  ],
  steps: [
    "Open Admin → **Usage Analytics** (`/usage-analytics`).",
    "Set the date range (start date and end date).",
    "If needed, filter to a specific organisationId.",
    "Review the totals and the trend chart to understand overall activity minutes.",
    "If you need a single customer view, use the customer selector (organisation + user) and review the per-customer series.",
    "Use **Export CSV** when you need to share data internally or do deeper analysis.",
    "If activity appears “missing”, allow for aggregation lag (rollups run on a schedule).",
  ],
  checklists: [
    {
      title: "Evidence checklist for an investigation",
      items: [
        "Date window used (start/end dates).",
        "organisationId (if relevant).",
        "Whether the activity might be demo usage or a spoof session (excluded).",
        "Approximate customer-reported timestamps (with timezone).",
      ],
    },
  ],
  whatSuccessLooksLike: [
    "You can clearly state whether the customer has recent active usage and how much.",
    "If needed, you can export a CSV and share it internally with minimal data.",
  ],
  troubleshooting: [
    {
      title: "Usage shows zero but the customer claims they were active",
      body: [
        "Confirm you are checking the correct date window (UTC boundaries may differ from local time).",
        "Allow for aggregation lag (usage is rolled up on a schedule).",
        "If still missing after a reasonable window, escalate with organisationId and timestamps.",
      ],
    },
    {
      title: "I can’t find the customer in the selector",
      body: [
        "The selector only shows customers present in the returned window. Expand the date range.",
        "If still missing, the customer may have no recorded usage minutes in the window.",
      ],
    },
  ],
  escalateWhen: [
    "Usage appears incorrect or missing after allowing for aggregation lag.",
    "You suspect demo usage or spoof-session exclusion is affecting your analysis but you can’t confirm.",
    "The Usage Analytics page fails to load consistently.",
  ],
  relatedGuides: [
    {
      label: "Investigate a submitted bug report",
      guideId: "bug-report-investigation",
    },
    { label: "Export users to CSV", guideId: "export-users-csv" },
  ],
  relatedAdminPaths: [
    { label: "Usage Analytics", path: "/usage-analytics" },
    { label: "Users", path: "/users" },
  ],
};
